import {
  MouseEvent as ReactMouseEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { Box, Button, Stack, Tooltip } from '@mui/material';
import {
  GridRowParams,
  GridRowSelectionModel,
  MuiEvent,
} from '@mui/x-data-grid-pro';

import { MasterIndexApi, MasterIndexApiPath } from '@/api';
import { IcInfoCircle } from '@/assets/images';
import { ConfirmationDialog, DataGrid } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { formatSortParam } from '@/components/TailwindComponents';
import { LocalStoragePrefix } from '@/constants';
import { isRmsCasesViewOnlyUser } from '@/services/service.kase';
import {
  useAgencyStore,
  useToastStore,
  useUserStore,
  ConfirmTypes,
} from '@/store';
import { getLocalStorageItem } from '@/utils';

import { relatedRecordsDataGridCols } from './data';
import {
  canOpenRelatedRecord,
  getRelatedRecordPath,
  openRelatedRecordInNewTab,
} from './RelatedRecordsCols';
import { printRelatedRecordsBatch } from './RelatedRecordsPrint/relatedRecordsBatchPrint';

export interface RelatedRecordsTableProps {
  recordId?: string;
  type?: MasterIndexApiPath;
}
export interface MenuState {
  menuEl: HTMLElement | null;
  selectedId?: string;
}

interface RelatedRecordGridRow {
  _id: string;
  name?: string;
  type?: string;
}

const isCaseRecordType = (type?: string) => {
  const normalizedType = type
    ?.trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

  return (
    normalizedType === 'CASE' ||
    normalizedType === 'KASE' ||
    normalizedType === 'FIRE_CASE' ||
    normalizedType === 'FIRE_KASE'
  );
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const RelatedRecordsTable = ({
  recordId,
  type,
}: RelatedRecordsTableProps) => {
  const { user, account } = useUserStore();
  const { currentAgency, agencies, fetchAgency } = useAgencyStore();
  const { updateToast } = useToastStore();
  const [currentRows, setCurrentRows] = useState<RelatedRecordGridRow[]>([]);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [restrictionOpen, setRestrictionOpen] = useState(false);
  const [restrictedRows, setRestrictedRows] = useState<RelatedRecordGridRow[]>(
    [],
  );
  const [pendingPrintableRows, setPendingPrintableRows] = useState<
    RelatedRecordGridRow[]
  >([]);

  const fetchMasterNameRelatedRecords = useCallback(
    async (
      page = 1,
      limit = 100,
      filter?: string,
      sort?: string,
    ): Promise<{ rows: any[]; totalCount: number }> => {
      try {
        const response = await MasterIndexApi.getMasterRelatedRecords(
          type as MasterIndexApiPath,
          recordId as string,
          {
            page,
            limit,
            filter,
            sort: formatSortParam(sort, { createdAt: -1 }),
          },
        );
        const { data } = response;
        const rows = data.docs as unknown as RelatedRecordGridRow[];

        setCurrentRows(rows);
        setRowSelectionModel((prevSelection) =>
          prevSelection.filter((selectedId) =>
            rows.some((row) => String(row._id) === String(selectedId)),
          ),
        );

        return {
          rows,
          totalCount: data.totalDocs,
        };
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
        setCurrentRows([]);
        setRowSelectionModel([]);
        return { rows: [], totalCount: 0 };
      }
    },
    [recordId, type, updateToast],
  );

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `related-records-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return relatedRecordsDataGridCols;
      }

      return mergeColumnSettings(relatedRecordsDataGridCols, savedSettings);
    },
  );

  const columns = useDataGridColumns({
    type: 'relatedRecords',
    columnSettings,
  });

  const selectedRows = useMemo(() => {
    const selectedIds = new Set(rowSelectionModel.map((id) => String(id)));
    return currentRows.filter((row) => selectedIds.has(String(row._id)));
  }, [currentRows, rowSelectionModel]);

  const hasSelectedCase = useMemo(
    () => selectedRows.some((row) => isCaseRecordType(row.type)),
    [selectedRows],
  );

  const restrictedDescription = useMemo(() => {
    if (!restrictedRows.length) {
      return '';
    }

    const list = restrictedRows
      .map(
        (row) =>
          `<li style="margin: 0 0 8px 0;">${escapeHtml(
            row.name || row._id,
          )}</li>`,
      )
      .join('');

    return `
      <p style="margin: 0 0 12px 0;">You don't have permission to print the following records:</p>
      <ul style="margin: 0 0 16px 0; padding-left: 24px; list-style-type: disc;">${list}</ul>
      <p style="margin: 0;">Do you want to proceed with printing unrestricted records?</p>
    `;
  }, [restrictedRows]);

  const handleSelectionChange = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      const currentIds = new Set(currentRows.map((row) => String(row._id)));
      const filteredSelection = newSelectionModel.filter((id) =>
        currentIds.has(String(id)),
      );
      setRowSelectionModel(filteredSelection);
    },
    [currentRows],
  );

  const handlePrintRows = useCallback(
    async (rowsToPrint: RelatedRecordGridRow[]) => {
      if (!rowsToPrint.length) {
        updateToast({
          open: true,
          type: ConfirmTypes.WARNING,
          message: 'No unrestricted records available to print.',
        });
        return;
      }

      setIsPrinting(true);
      try {
        const { failedRecords, printedCount } = await printRelatedRecordsBatch({
          rows: rowsToPrint,
          user,
          currentAgency,
          agencies,
          fetchAgency,
        });

        if (!printedCount) {
          updateToast({
            open: true,
            type: ConfirmTypes.ERROR,
            message: 'No records were printed.',
          });
          return;
        }

        if (failedRecords.length) {
          updateToast({
            open: true,
            type: ConfirmTypes.WARNING,
            message: `Some records were skipped: ${failedRecords.join(', ')}`,
          });
        }

        setRowSelectionModel([]);
      } catch (err: any) {
        updateToast({
          open: true,
          type: ConfirmTypes.ERROR,
          message: err?.message || 'Failed to print selected records.',
        });
      } finally {
        setIsPrinting(false);
      }
    },
    [agencies, currentAgency, fetchAgency, updateToast, user],
  );

  const handlePrintClick = useCallback(async () => {
    if (!selectedRows.length || isPrinting) {
      return;
    }

    const printableRows: RelatedRecordGridRow[] = [];
    const inaccessibleRows: RelatedRecordGridRow[] = [];

    selectedRows.forEach((row) => {
      if (canOpenRelatedRecord(row, user, account)) {
        printableRows.push(row);
      } else {
        inaccessibleRows.push(row);
      }
    });

    if (inaccessibleRows.length) {
      setRestrictedRows(inaccessibleRows);
      setPendingPrintableRows(printableRows);
      setRestrictionOpen(true);
      return;
    }

    await handlePrintRows(printableRows);
  }, [account, handlePrintRows, isPrinting, selectedRows, user]);

  const handleRestrictionCancel = useCallback(() => {
    setRestrictionOpen(false);
    setRestrictedRows([]);
    setPendingPrintableRows([]);
  }, []);

  const handleRestrictionConfirm = useCallback(async () => {
    setRestrictionOpen(false);
    await handlePrintRows(pendingPrintableRows);
    setRestrictedRows([]);
    setPendingPrintableRows([]);
  }, [handlePrintRows, pendingPrintableRows]);

  const handleRowClick = (
    params: GridRowParams,
    event: MuiEvent<ReactMouseEvent>,
  ) => {
    if (
      event.target instanceof HTMLElement &&
      (event.target.tagName === 'BUTTON' ||
        event.target.closest('button') ||
        event.target.closest('[role="button"]') ||
        event.target.closest('[role="checkbox"]') ||
        event.target.closest('.MuiDataGrid-columnHeaderCheckbox') ||
        event.target.closest('a'))
    ) {
      return;
    }

    const path = getRelatedRecordPath(params.row);
    if (!path) {
      return;
    }

    if (!canOpenRelatedRecord(params.row, user, account)) {
      updateToast({
        open: true,
        message: 'You do not have permission to open this related record.',
      });
      return;
    }

    openRelatedRecordInNewTab(params.row);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <DataGrid
        key="related-records-table"
        name="related-records-table"
        fetchData={fetchMasterNameRelatedRecords}
        columns={columns}
        columnSettings={columnSettings}
        setColumnSettings={setColumnSettings}
        onRowClick={handleRowClick}
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={handleSelectionChange}
        sx={{
          '& .MuiDataGrid-columnHeaderCheckbox:focus, & .MuiDataGrid-columnHeaderCheckbox:focus-within':
            {
              outline: 'none !important',
            },
          '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover':
            {
              backgroundColor: 'transparent !important',
            },
          '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell': {
            backgroundColor: 'transparent !important',
          },
          '& .MuiDataGrid-footerContainer': {
            backgroundColor: 'background.paper',
          },
          '& .MuiDataGrid-selectedRowCount': {
            backgroundColor: 'transparent',
          },
        }}
        cta={
          selectedRows.length && !isRmsCasesViewOnlyUser(user) ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ order: -1, flexShrink: 0 }}
            >
              {hasSelectedCase && (
                <Tooltip
                  title="Printing unredacted. To print with redactions, print records individually."
                  placement="bottom"
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IcInfoCircle color="#143A5A" />
                  </Box>
                </Tooltip>
              )}
              <Button
                variant="contained"
                color="error"
                onClick={handlePrintClick}
                disabled={isPrinting}
              >
                {isPrinting ? 'Printing...' : 'Print'}
              </Button>
              <Box sx={{ width: 4, flexShrink: 0 }} />
            </Stack>
          ) : undefined
        }
      />
      <ConfirmationDialog
        open={restrictionOpen}
        title="Printing Restriction"
        description={restrictedDescription}
        confirmText="OK"
        cancelText="Cancel"
        onCancel={handleRestrictionCancel}
        onConfirm={handleRestrictionConfirm}
      />
    </Box>
  );
};
